/**
 * GlobeCanvas — SVG stipple/wireframe globe (cycle-109 v2 sprint-3 S3.1).
 *
 * Ported from `output/design_reference/design_handoff_echelon_update/workspace/globe.jsx`
 * (cycle-109 v2 PRD FR-5, sprint-plan AC-3.1.a-e, sprint-3-globe-cleanup-plan.md §2).
 *
 * Architectural lock L-3: NO `three`, `globe.gl`, `@deck.gl/*`, `maplibre-gl`,
 * `topojson-client` imports. Pure SVG. Hand-curated LAND_POLYGONS — no geo lib.
 *
 * Pure-math helpers (LAND_POLYGONS, isLand, STIPPLE_POINTS, GRATICULE, project,
 * shortestRotDelta, easeInOutCubic, BEACON_SIZE_BY_SEV, beaconScale) are
 * exported for vitest unit tests under `__tests__/GlobeCanvas.test.tsx`.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CanvasMode, ViewState } from '../../hooks/useWorkspaceState';
import type { WorldMonitorLiveResponse, WorldMonitorSignal } from '../../api/worldMonitor';
import type { LayerState } from '../../hooks/useLayerState';
import { isDomainVisible, type GlobeLayerState } from '../../hooks/useGlobeLayerState';
import type { InfrastructurePoint } from '../../hooks/useInfrastructureLayers';
import { normalizeSignal, type NormalizedSignal } from '../../domain/signals/normalizeSignal';

// ── Constants ──────────────────────────────────────────────────────────

/**
 * Hand-curated 8 continental approximations.
 * Per Q-S3.0-b (operator decision 2026-05-26): no topojson, no d3-geo.
 * Visual impression only — geographic accuracy intentionally not required.
 * Polygons in [lon, lat] order. Source: design ref globe.jsx:9-26.
 */
export const LAND_POLYGONS: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = [
  // North America
  [[-160, 65], [-140, 70], [-100, 72], [-75, 60], [-55, 50], [-70, 32], [-95, 18], [-110, 22], [-125, 40], [-135, 55], [-160, 65]],
  // South America
  [[-80, 12], [-60, 10], [-50, -5], [-40, -20], [-55, -40], [-72, -55], [-78, -20], [-80, 0], [-80, 12]],
  // Europe + western Russia
  [[-10, 38], [5, 36], [25, 36], [40, 40], [55, 50], [70, 66], [40, 70], [15, 72], [0, 58], [-10, 50], [-10, 38]],
  // Africa
  [[-15, 35], [10, 36], [32, 31], [45, 12], [50, -5], [40, -30], [18, -35], [10, -10], [-8, 5], [-15, 20], [-15, 35]],
  // Asia
  [[55, 50], [80, 50], [110, 55], [140, 60], [145, 45], [125, 30], [105, 15], [80, 10], [70, 22], [55, 35], [55, 50]],
  // India subcontinent
  [[68, 24], [78, 8], [88, 12], [92, 22], [85, 28], [72, 28], [68, 24]],
  // SE Asia / Indonesia
  [[95, 5], [115, -2], [130, -5], [140, -8], [120, -10], [100, -2], [95, 5]],
  // Australia
  [[113, -22], [130, -12], [145, -18], [152, -28], [145, -38], [120, -34], [113, -22]],
];

/** Domain → color token (matches design ref fixtures.jsx DOMAIN_COLOR). */
export const DOMAIN_COLOR: Record<string, string> = {
  maritime: 'var(--sig-azure)',
  finance: 'var(--sig-ember)',
  seismic: 'var(--sig-rose)',
  aviation: 'var(--sig-iris)',
  cyber: 'var(--sig-iris)',
  environmental: 'var(--sig-mint)',
  geopolitical: 'var(--sig-ember)',
  // Extended for SignalDomain values not in design ref but present in backend
  health: 'var(--sig-mint)',
  nuclear: 'var(--sig-rose)',
  academic: 'var(--sig-iris)',
  corporate: 'var(--sig-ember)',
  legal: 'var(--sig-amber)',
  sports: 'var(--sig-amber)',
  other: 'var(--fg-muted)',
};

/** Severity → halo color token (matches design ref SEV_COLOR). */
export const SEV_COLOR: Record<string, string> = {
  critical: 'var(--sig-rose)',
  high: 'var(--sig-ember)',
  moderate: 'var(--sig-amber)',
  low: 'var(--sig-mint)',
};

/** Beacon radius by severity tier (design ref globe.jsx:73). */
export const BEACON_SIZE_BY_SEV: Record<string, number> = {
  critical: 5.5,
  high: 4.2,
  moderate: 3.3,
  low: 2.8,
};

// ── Pure-math helpers (exported for vitest) ────────────────────────────

/**
 * Ray-casting point-in-polygon test.
 * Polygons stored as [lon, lat] pairs but compared on (lat, lon) — matches
 * design ref globe.jsx:28-39 by treating the first coord as `xi` and second
 * as `yi`, indexing yi against lat and xi against lon.
 */
export function isLand(lat: number, lon: number): boolean {
  for (const poly of LAND_POLYGONS) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const [xi, yi] = poly[i];
      const [xj, yj] = poly[j];
      if (((yi > lat) !== (yj > lat)) && (lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) {
        inside = !inside;
      }
    }
    if (inside) return true;
  }
  return false;
}

/**
 * Pre-sampled lat/lon grid at 3° resolution, land-only.
 * Built once at module load (~5k points). Design ref globe.jsx:42-50.
 */
export const STIPPLE_POINTS: ReadonlyArray<readonly [number, number]> = (() => {
  const pts: Array<[number, number]> = [];
  for (let lat = -75; lat <= 80; lat += 3) {
    for (let lon = -180; lon <= 180; lon += 3) {
      if (isLand(lat, lon)) pts.push([lat, lon]);
    }
  }
  return pts;
})();

/**
 * Graticule lines — meridians every 30°, parallels every 20°.
 * Each line is a polyline of (lat, lon) samples for projection.
 * Design ref globe.jsx:53-68.
 */
export const GRATICULE: ReadonlyArray<ReadonlyArray<readonly [number, number]>> = (() => {
  const lines: Array<Array<[number, number]>> = [];
  // Meridians
  for (let lon = -150; lon <= 180; lon += 30) {
    const line: Array<[number, number]> = [];
    for (let lat = -80; lat <= 80; lat += 4) line.push([lat, lon]);
    lines.push(line);
  }
  // Parallels
  for (let lat = -60; lat <= 60; lat += 20) {
    const line: Array<[number, number]> = [];
    for (let lon = -180; lon <= 180; lon += 4) line.push([lat, lon]);
    lines.push(line);
  }
  return lines;
})();

export interface Projected {
  x: number;
  y: number;
  visible: boolean;
  depth: number;
}

/**
 * Project lat/lon to SVG (x, y) on a unit-sphere oriented by rotation `rot`
 * (degrees, y-axis) and a fixed 20° tilt.
 * Returns visible = false if the point is on the far hemisphere (z2 < -0.15).
 * Matches design ref tokens.jsx:12-29.
 */
export function project(lat: number, lon: number, cx: number, cy: number, r: number, rot: number): Projected {
  const lonRad = ((lon + rot) * Math.PI) / 180;
  const latRad = (lat * Math.PI) / 180;
  const tilt = (20 * Math.PI) / 180;
  const x = Math.cos(latRad) * Math.sin(lonRad);
  const y = Math.sin(latRad);
  const z = Math.cos(latRad) * Math.cos(lonRad);
  // Apply x-axis tilt
  const y2 = y * Math.cos(tilt) - z * Math.sin(tilt);
  const z2 = y * Math.sin(tilt) + z * Math.cos(tilt);
  return {
    x: cx + x * r,
    y: cy - y2 * r,
    visible: z2 > -0.15,
    depth: z2,
  };
}

/**
 * Shortest angular delta from `from` to `to` in degrees,
 * wrapped to (-180, 180]. Crosses the dateline correctly.
 * Idempotent at zero; symmetric in sign. Design ref globe.jsx:113-116.
 */
export function shortestRotDelta(from: number, to: number): number {
  return (((to - from) % 360) + 540) % 360 - 180;
}

/**
 * Cubic ease-in-out (Penner-style). Used for the 900ms fly-to tween.
 * easeInOutCubic(0) === 0, easeInOutCubic(1) === 1, easeInOutCubic(0.5) === 0.5.
 * Design ref globe.jsx:117.
 */
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/** Perspective scale derived from a projected point's depth (-1..1). */
export function beaconScale(depth: number): number {
  return 0.6 + 0.4 * depth;
}

/** Final beacon SVG radius = severity-tier × perspective-scale. */
export function beaconRadius(severity: string, depth: number): number {
  const base = BEACON_SIZE_BY_SEV[severity] ?? 3;
  return base * beaconScale(depth);
}

/**
 * Beacon base opacity from projected depth (FR-4). Monotonic across the visible
 * depth range [-0.15, 1] → [0.30, 1.00] — matches the stipple ramp; no flat 0.4
 * floor (the old `Math.max(0.4, depth)` defeated the perspective fade). Strictly
 * increasing in depth (slope 0.7/1.15 > 0).
 */
export function beaconOpacity(depth: number): number {
  return 0.3 + 0.7 * ((depth + 0.15) / 1.15);
}

/**
 * Great-circle proximity threshold (SDD §9.3): 8° of central angle (≈890 km) —
 * the angular span where two beacon cores visually merge into one cluster. The
 * single named knob the perf path (§5.4) escalates; never replaced by a lib (L-3).
 */
export const GREAT_CIRCLE_CLUSTER_DEG = 8;

/**
 * Great-circle central angle between two lat/lon points, in DEGREES (haversine).
 * Replaces the old Euclidean √(Δlat²+Δlon²), which was pole-broken (lon degrees
 * shrink with latitude) and wrong across the antimeridian (Δlon 359° vs 1°).
 * Antimeridian-correct: `sin(dLam/2)²` wraps (lon 179 vs −179 → ~2°). Pole-correct:
 * the `cos(φ)` weighting collapses the lon contribution near the poles.
 */
export function angularDistance(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const phi1 = toRad(aLat);
  const phi2 = toRad(bLat);
  const dPhi = toRad(bLat - aLat);
  const dLam = toRad(bLon - aLon);
  const h = Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLam / 2) ** 2;
  return (2 * Math.asin(Math.min(1, Math.sqrt(h))) * 180) / Math.PI; // central angle in degrees
}

/** True when two points cluster within `thresholdDeg` of great-circle arc (strict `<`). */
export function isProximityCluster(
  aLat: number,
  aLon: number,
  bLat: number,
  bLon: number,
  thresholdDeg = GREAT_CIRCLE_CLUSTER_DEG,
): boolean {
  return angularDistance(aLat, aLon, bLat, bLon) < thresholdDeg;
}

// ── Props ──────────────────────────────────────────────────────────────

export type GlobeStyle = 'stipple' | 'wireframe';

export interface FocusInvestigation {
  id: string;
  lat: number;
  lon: number;
  zoom?: number;
  triggerIds?: string[];
  accent?: string;
  code?: string;
  region?: string;
}

export interface GlobeCanvasProps {
  /** Canvas mode (preserved for legacy consumers; takeover surface ignores). */
  mode?: CanvasMode;
  /** Scope-transition callback (preserved for upstream compat; S3.6 may replace). */
  onScopeTransition?: (viewState: ViewState) => void;
  /** Live signal corpus. */
  liveData: WorldMonitorLiveResponse;
  /** Legacy scope-mode layer toggles (kept for prop-signature compat). */
  layers?: LayerState;
  /** Globe-mode layer toggles. S3.1 reads but does not enforce shape — S3.2 flattens to 10. */
  globeLayers?: GlobeLayerState;
  /** Infrastructure overlay points. */
  infrastructurePoints?: InfrastructurePoint[];
  /** Fly-to target. Provide to focus; null/undefined to release. */
  focusInv?: FocusInvestigation | null;
  /** SVG style variant. */
  style?: GlobeStyle;
  /** Render correlation arcs between connected signals. */
  showArcs?: boolean;
  /** Auto-rotate state at mount (parent controls). Defaults to false. */
  autoRotate?: boolean;
  /** Beacon click handler. */
  onSelectSignal?: (signal: WorldMonitorSignal) => void;
}

// ── Beacon subcomponent ────────────────────────────────────────────────

interface BeaconProps {
  x: number;
  y: number;
  depth: number;
  signal: WorldMonitorSignal;
  /** D4 canonical_signal_id (`osint:{id}`) from normalizeSignal() — FR-2/7. */
  canonicalSignalId: string;
  selected: boolean;
  highlighted: boolean;
  dimmed: boolean;
  emphasized: boolean;
  emphasisColor: string;
  onClick?: (signal: WorldMonitorSignal) => void;
  onHover?: (signal: WorldMonitorSignal | null) => void;
}

function Beacon({
  x, y, depth, signal, canonicalSignalId,
  selected, highlighted, dimmed, emphasized, emphasisColor,
  onClick, onHover,
}: BeaconProps) {
  const color = DOMAIN_COLOR[signal.domain] ?? 'var(--sig-mint)';
  const sevColor = SEV_COLOR[signal.severity] ?? 'var(--sig-mint)';
  const scale = beaconScale(depth);
  const base = BEACON_SIZE_BY_SEV[signal.severity] ?? 3;
  const baseOpacity = beaconOpacity(depth);
  const opacity = dimmed ? baseOpacity * 0.18 : baseOpacity;
  const isHot = signal.severity === 'critical' || signal.severity === 'high';
  const emphR = emphasized ? base * scale * 1.15 : base * scale;
  return (
    <g
      data-beacon="1"
      data-testid={`globe-beacon-${canonicalSignalId}`}
      data-signal-id={canonicalSignalId}
      transform={`translate(${x} ${y})`}
      style={{ cursor: 'pointer', opacity, transition: 'opacity 400ms var(--ease-out)' }}
      onClick={() => onClick?.(signal)}
      onMouseEnter={() => onHover?.(signal)}
      onMouseLeave={() => onHover?.(null)}
    >
      {/* Emphasis halo (focused investigation) */}
      {emphasized && (
        <>
          <circle r={emphR + 8} fill="none" stroke={emphasisColor || color} strokeWidth="0.7" opacity="0.35" />
          <circle r={emphR + 4} fill="none" stroke={emphasisColor || color} strokeWidth="1.2" opacity="0.85" />
        </>
      )}
      {/* Severity halo (critical/high) */}
      {isHot && !dimmed && (
        <circle r={base * scale} fill="none" stroke={sevColor} strokeWidth="0.8" opacity="0.6" />
      )}
      {/* Selected ring */}
      {(selected || highlighted) && (
        <circle r={base * scale + 5} fill="none" stroke={color} strokeWidth="1" opacity="0.9" />
      )}
      {/* Core */}
      <circle r={emphR} fill={color} opacity={isHot ? 0.95 : 0.85} />
      <circle r={emphR * 0.5} fill="#fff" opacity="0.7" />
    </g>
  );
}

// ── Main component ─────────────────────────────────────────────────────

export function GlobeCanvas({
  liveData,
  globeLayers,
  focusInv,
  style = 'stipple',
  showArcs = true,
  autoRotate = false,
  onSelectSignal,
}: GlobeCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ x: number; startRot: number } | null>(null);
  const flyRef = useRef<{ raf: number } | null>(null);
  const prevViewRef = useRef<{ rot: number; zoom: number; spinning: boolean } | null>(null);
  const rafRef = useRef<number | undefined>(undefined);
  const lastTsRef = useRef<number>(0);

  const [rot, setRot] = useState(20);
  const [zoom, setZoom] = useState(1.0);
  const [spinning, setSpinning] = useState(!!autoRotate);
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [size, setSize] = useState({ w: 800, h: 800 });

  // Sync upstream autoRotate prop → local spinning state.
  useEffect(() => {
    setSpinning(!!autoRotate);
  }, [autoRotate]);

  // Fly-to on focusInv change with prev-view restoration.
  // Uses focusInv.id as dep key so identical-object passes are no-ops.
  useEffect(() => {
    // Cancel any in-flight tween.
    if (flyRef.current) {
      cancelAnimationFrame(flyRef.current.raf);
      flyRef.current = null;
    }

    if (focusInv) {
      if (!prevViewRef.current) {
        prevViewRef.current = { rot, zoom, spinning };
      }
      setSpinning(false);
      const targetRot = -focusInv.lon;
      const targetZoom = focusInv.zoom ?? 2.2;
      const startRot = rot;
      const startZoom = zoom;
      const delta = shortestRotDelta(startRot, targetRot);
      const duration = 900;
      const t0 = performance.now();
      const step = (now: number) => {
        if (dragRef.current) {
          flyRef.current = null;
          return;
        }
        const t = Math.min(1, (now - t0) / duration);
        const e = easeInOutCubic(t);
        setRot(startRot + delta * e);
        setZoom(startZoom + (targetZoom - startZoom) * e);
        if (t < 1) flyRef.current = { raf: requestAnimationFrame(step) };
        else flyRef.current = null;
      };
      flyRef.current = { raf: requestAnimationFrame(step) };
    } else if (prevViewRef.current) {
      const { rot: pRot, zoom: pZoom, spinning: pSpin } = prevViewRef.current;
      prevViewRef.current = null;
      const startRot = rot;
      const startZoom = zoom;
      const delta = shortestRotDelta(startRot, pRot);
      const duration = 700;
      const t0 = performance.now();
      const step = (now: number) => {
        if (dragRef.current) {
          flyRef.current = null;
          return;
        }
        const t = Math.min(1, (now - t0) / duration);
        const e = easeInOutCubic(t);
        setRot(startRot + delta * e);
        setZoom(startZoom + (pZoom - startZoom) * e);
        if (t < 1) {
          flyRef.current = { raf: requestAnimationFrame(step) };
        } else {
          flyRef.current = null;
          setSpinning(pSpin);
        }
      };
      flyRef.current = { raf: requestAnimationFrame(step) };
    }

    return () => {
      if (flyRef.current) {
        cancelAnimationFrame(flyRef.current.raf);
        flyRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusInv?.id]);

  // ResizeObserver to track container size.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Auto-rotate loop — paused while dragging.
  useEffect(() => {
    if (!spinning || dragging) return;
    const tick = (ts: number) => {
      if (lastTsRef.current) {
        const dt = (ts - lastTsRef.current) / 1000;
        setRot((r) => (r + dt * 3) % 360);
      }
      lastTsRef.current = ts;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== undefined) cancelAnimationFrame(rafRef.current);
      lastTsRef.current = 0;
    };
  }, [spinning, dragging]);

  const cx = size.w / 2;
  const cy = size.h / 2;
  const r = Math.min(size.w, size.h) * 0.42 * zoom;

  // Full normalized corpus (FR-7) — drives canonical ids + the FR-9 count.
  const normalizedSignals = useMemo<NormalizedSignal[]>(
    () => (liveData?.signals ?? []).map(normalizeSignal),
    [liveData],
  );

  // ── Render set: geo-plottable signals filtered by globe layer state (FR-3) ──
  const signals = useMemo<WorldMonitorSignal[]>(() => {
    const raw = liveData?.signals ?? [];
    // Defensive filter for missing / placeholder (0,0) geo and out-of-range.
    const geoFiltered = raw.filter(
      (s) =>
        s.geo != null &&
        !(s.geo.lat === 0 && s.geo.lon === 0) &&
        Math.abs(s.geo.lat) <= 90 &&
        Math.abs(s.geo.lon) <= 180,
    );
    if (!globeLayers) return geoFiltered;
    // FR-3: apply the same predicate the feed uses so one filtered set governs
    // beacons + arcs + counts. isDomainVisible returns true for `other`/unmapped
    // domains, keeping uncategorised signals visible.
    return geoFiltered.filter((s) => isDomainVisible(s.domain, globeLayers));
  }, [liveData, globeLayers]);

  const stippleDots = useMemo(() => {
    return STIPPLE_POINTS.map(([lat, lon]) => {
      const p = project(lat, lon, cx, cy, r, rot);
      if (!p.visible) return null;
      return { x: p.x, y: p.y, depth: p.depth };
    }).filter((p): p is { x: number; y: number; depth: number } => p !== null);
  }, [cx, cy, r, rot]);

  const graticulePaths = useMemo(() => {
    return GRATICULE.map((line) => {
      const segs: string[] = [];
      let cur = '';
      for (const [lat, lon] of line) {
        const p = project(lat, lon, cx, cy, r, rot);
        if (p.visible) {
          cur += `${cur ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)} `;
        } else if (cur) {
          segs.push(cur);
          cur = '';
        }
      }
      if (cur) segs.push(cur);
      return segs;
    }).flat();
  }, [cx, cy, r, rot]);

  const wirePaths = useMemo(() => {
    if (style !== 'wireframe') return [] as string[];
    return LAND_POLYGONS.map((poly) => {
      let d = '';
      let cur = '';
      for (const [lon, lat] of poly) {
        const p = project(lat, lon, cx, cy, r, rot);
        if (p.visible) {
          cur += `${cur ? 'L' : 'M'}${p.x.toFixed(1)} ${p.y.toFixed(1)} `;
        } else if (cur) {
          d += cur + ' ';
          cur = '';
        }
      }
      d += cur;
      return d;
    });
  }, [style, cx, cy, r, rot]);

  // Beacons — projected, front-hemisphere-only. canonicalSignalId derives from
  // the shared normalizeSignal() adapter (FR-2/7), not the raw WM id.
  const beacons = useMemo(() => {
    return signals
      .map((s) => ({
        signal: s,
        canonicalSignalId: normalizeSignal(s).canonical_signal_id,
        ...project(s.geo.lat, s.geo.lon, cx, cy, r, rot),
      }))
      .filter((b) => b.visible);
  }, [signals, cx, cy, r, rot]);

  // Focus set for emphasis vs dim.
  const focusIds = focusInv?.triggerIds ? new Set(focusInv.triggerIds) : null;
  const emphColor = focusInv?.accent || 'var(--sig-mint)';

  // Correlation arcs between front-hemisphere signal pairs.
  const arcs = useMemo(() => {
    if (!showArcs) return [] as Array<{ key: string; d: string; color: string }>;
    const out: Array<{ key: string; d: string; color: string }> = [];
    const seen = new Set<string>();
    const byId = new Map(signals.map((s) => [s.id, s]));
    for (const s of signals) {
      for (const cid of s.correlated_signals ?? []) {
        const t = byId.get(cid);
        if (!t) continue;
        const key = s.id < cid ? `${s.id}:${cid}` : `${cid}:${s.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const pa = project(s.geo.lat, s.geo.lon, cx, cy, r, rot);
        const pb = project(t.geo.lat, t.geo.lon, cx, cy, r, rot);
        if (!pa.visible || !pb.visible) continue;
        const mx = (pa.x + pb.x) / 2;
        const my = (pa.y + pb.y) / 2 - Math.hypot(pa.x - pb.x, pa.y - pb.y) * 0.35;
        out.push({
          key,
          d: `M${pa.x} ${pa.y} Q${mx} ${my} ${pb.x} ${pb.y}`,
          color: DOMAIN_COLOR[s.domain] ?? 'var(--sig-mint)',
        });
      }
    }
    return out;
  }, [signals, showArcs, cx, cy, r, rot]);

  // Trigger-chain arcs — sequential between focusInv.triggerIds.
  const chainArcs = useMemo(() => {
    if (!focusInv?.triggerIds || focusInv.triggerIds.length < 2) return [];
    const out: Array<{ key: string; d: string }> = [];
    const byId = new Map(signals.map((s) => [s.id, s]));
    const ids = focusInv.triggerIds;
    for (let i = 0; i < ids.length - 1; i++) {
      const a = byId.get(ids[i]);
      const b = byId.get(ids[i + 1]);
      if (!a || !b) continue;
      const pa = project(a.geo.lat, a.geo.lon, cx, cy, r, rot);
      const pb = project(b.geo.lat, b.geo.lon, cx, cy, r, rot);
      if (!pa.visible || !pb.visible) continue;
      const mx = (pa.x + pb.x) / 2;
      const my = (pa.y + pb.y) / 2 - Math.hypot(pa.x - pb.x, pa.y - pb.y) * 0.4;
      out.push({ key: `chain-${a.id}-${b.id}`, d: `M${pa.x} ${pa.y} Q${mx} ${my} ${pb.x} ${pb.y}` });
    }
    return out;
  }, [focusInv?.triggerIds, signals, cx, cy, r, rot]);

  // Investigation breathing-ring halo at focus center.
  const invHalo = focusInv ? project(focusInv.lat, focusInv.lon, cx, cy, r, rot) : null;

  // ── Event handlers ────────────────────────────────────────────────────

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = -e.deltaY * 0.001;
    setZoom((z) => Math.max(0.5, Math.min(4, z + delta)));
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    if (target.closest('[data-beacon="1"]')) return;
    dragRef.current = { x: e.clientX, startRot: rot };
    setDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.x;
    setRot((dragRef.current.startRot + dx * 0.4) % 360);
  };

  const handleMouseUp = () => {
    dragRef.current = null;
    setDragging(false);
  };

  const handleBeaconClick = (signal: WorldMonitorSignal) => {
    setSelected(signal.id);
    onSelectSignal?.(signal);
  };

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div
      ref={containerRef}
      data-globe-canvas
      data-globe-style={style}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        cursor: dragging ? 'grabbing' : 'grab',
      }}
    >
      {/* Atmospheric halo (CSS radial gradient behind globe) */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at ${cx}px ${cy}px, var(--globe-halo) 0%, transparent ${r * 1.4}px)`,
          pointerEvents: 'none',
        }}
      />

      <svg width="100%" height="100%" style={{ position: 'absolute', inset: 0 }}>
        <defs>
          {/* Designed sphere surface (handoff --globe-surface, Workspace.html:30/:94)
              via --globe-sphere-0/1 stops. Handoff gradients end at 70% (dark) /
              75% (light); SVG `offset` is an attribute and cannot be themed via
              CSS — fixed 72% midpoint, visually negligible deviation. */}
          <radialGradient id="globe-sphere" cx="35%" cy="30%">
            <stop offset="0%" stopColor="var(--globe-sphere-0)" />
            <stop offset="72%" stopColor="var(--globe-sphere-1)" />
            <stop offset="100%" stopColor="var(--globe-sphere-1)" />
          </radialGradient>
          <radialGradient id="globe-rim" cx="50%" cy="50%">
            <stop offset="80%" stopColor="transparent" />
            <stop offset="98%" stopColor="var(--border-accent)" stopOpacity="0.4" />
            <stop offset="100%" stopColor="transparent" />
          </radialGradient>
        </defs>

        {/* Sphere */}
        <circle cx={cx} cy={cy} r={r} fill="url(#globe-sphere)" />
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--border-accent)" strokeWidth="0.7" opacity="0.6" />

        {/* Graticule */}
        {graticulePaths.map((d, i) => (
          <path
            key={`grat-${i}`}
            d={d}
            fill="none"
            stroke="var(--border)"
            strokeWidth="0.5"
            opacity={style === 'wireframe' ? 0.8 : 0.3}
          />
        ))}

        {/* Land — stipple variant */}
        {style === 'stipple' && (
          <g>
            {stippleDots.map((dot, i) => (
              <circle
                key={`stip-${i}`}
                cx={dot.x}
                cy={dot.y}
                r={0.8 + 0.4 * dot.depth}
                fill="var(--globe-land)"
                opacity={0.3 + 0.7 * dot.depth}
              />
            ))}
          </g>
        )}

        {/* Land — wireframe variant */}
        {style === 'wireframe' && (
          <g>
            {wirePaths.map((d, i) => (
              <path
                key={`wire-${i}`}
                d={d}
                fill="none"
                stroke="var(--globe-land-bright)"
                strokeWidth="0.9"
                opacity="0.75"
              />
            ))}
          </g>
        )}

        {/* Correlation arcs (behind beacons; dimmed when focus active) */}
        {arcs.map((a) => (
          <path
            key={a.key}
            d={a.d}
            fill="none"
            stroke={a.color}
            strokeWidth="0.7"
            strokeDasharray="2 3"
            opacity={focusIds ? 0.12 : 0.55}
            style={{ transition: 'opacity 400ms var(--ease-out)' }}
          />
        ))}

        {/* Investigation halo */}
        {invHalo && invHalo.visible && (
          <g transform={`translate(${invHalo.x} ${invHalo.y})`} style={{ pointerEvents: 'none' }}>
            <circle r="22" fill="none" stroke={emphColor} strokeWidth="0.8" opacity="0.3" />
            <circle r="14" fill="none" stroke={emphColor} strokeWidth="1.1" opacity="0.6" />
            <circle r="2" fill={emphColor} opacity="0.9" />
          </g>
        )}

        {/* Trigger chain arcs */}
        {chainArcs.map((a) => (
          <path
            key={a.key}
            d={a.d}
            fill="none"
            stroke={emphColor}
            strokeWidth="1.3"
            strokeDasharray="4 3"
            opacity="0.9"
          />
        ))}

        {/* Beacons */}
        {beacons.map((b) => {
          const inFocus = focusIds ? focusIds.has(b.signal.id) : false;
          return (
            <Beacon
              key={b.signal.id}
              x={b.x}
              y={b.y}
              depth={b.depth}
              signal={b.signal}
              canonicalSignalId={b.canonicalSignalId}
              selected={selected === b.signal.id}
              highlighted={hover === b.signal.id}
              dimmed={!!focusIds && !inFocus}
              emphasized={inFocus}
              emphasisColor={emphColor}
              onHover={(s) => setHover(s?.id ?? null)}
              onClick={handleBeaconClick}
            />
          );
        })}

        {/* Rim light */}
        <circle cx={cx} cy={cy} r={r} fill="url(#globe-rim)" pointerEvents="none" />
      </svg>

      {/* Control panel (auto-rotate toggle + zoom controls) */}
      <div
        style={{
          position: 'absolute',
          right: 16,
          bottom: 42,
          zIndex: 3,
          display: 'flex',
          flexDirection: 'column',
          width: 30,
          background: 'var(--bg-panel)',
          border: '1px solid var(--border)',
          boxShadow: 'var(--shadow-1)',
        }}
      >
        <button
          type="button"
          title={spinning ? 'Pause rotation' : 'Resume rotation'}
          onClick={() => setSpinning((s) => !s)}
          style={{
            width: 30,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: '1px solid var(--border)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: spinning ? 'var(--sig-mint)' : 'transparent',
              border: `1.25px solid ${spinning ? 'var(--sig-mint)' : 'var(--fg-muted)'}`,
              boxShadow: spinning
                ? '0 0 6px color-mix(in oklch, var(--sig-mint) 60%, transparent)'
                : 'none',
              transition: 'background .18s var(--ease-out), box-shadow .18s var(--ease-out)',
            }}
          />
        </button>
        <button
          type="button"
          title="Zoom in"
          onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
          style={{
            width: 30,
            height: 26,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            color: 'var(--fg-secondary)',
            borderBottom: '1px solid var(--border)',
            fontWeight: 300,
            lineHeight: 1,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          +
        </button>
        <div
          className="mono tabular"
          title={`zoom ${zoom.toFixed(2)}×`}
          style={{
            width: 30,
            textAlign: 'center',
            fontSize: 9,
            color: 'var(--fg-secondary)',
            padding: '3px 0 2px',
            borderBottom: '1px solid var(--border)',
            letterSpacing: '0.04em',
          }}
        >
          {zoom.toFixed(1)}×
        </div>
        <button
          type="button"
          title="Zoom out"
          onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))}
          style={{
            width: 30,
            height: 26,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 14,
            color: 'var(--fg-secondary)',
            borderBottom: '1px solid var(--border)',
            fontWeight: 300,
            lineHeight: 1,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          −
        </button>
        <button
          type="button"
          title="Reset view"
          onClick={() => {
            setZoom(1);
            setRot(20);
          }}
          className="mono"
          style={{
            width: 30,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 8.5,
            color: 'var(--fg-muted)',
            letterSpacing: '0.08em',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          1:1
        </button>
      </div>

      {/* Bottom-left readout */}
      <div
        className="mono"
        style={{
          position: 'absolute',
          left: 20,
          bottom: 18,
          fontSize: 10,
          color: 'var(--fg-muted)',
          letterSpacing: '0.08em',
          pointerEvents: 'none',
        }}
      >
        GLOBE · {spinning ? 'LIVE' : 'HOLD'} · ROT {rot.toFixed(1)}° · ZOOM {zoom.toFixed(1)}×
      </div>
      {/* Bottom-right summary */}
      <div
        className="mono"
        style={{
          position: 'absolute',
          right: 20,
          bottom: 18,
          fontSize: 10,
          color: 'var(--fg-muted)',
          letterSpacing: '0.08em',
          pointerEvents: 'none',
          textAlign: 'right',
        }}
      >
        {signals.length} SIGNALS ·{' '}
        {signals.filter((s) => s.severity === 'critical').length} CRIT ·{' '}
        {signals.filter((s) => s.severity === 'high').length} HIGH
        {normalizedSignals.some((s) => s.geo === null) && (
          <>
            {' · '}
            {normalizedSignals.filter((s) => s.geo === null).length} NOT PLOTTABLE
          </>
        )}
      </div>
    </div>
  );
}

export default GlobeCanvas;
